import axios from 'axios';
import { useCallback, useState } from 'react';

import { useAxios } from '@/hooks/useAxios';
import { useAddSeeds } from '@/hooks/useSeeds';
import {
  type BrokerPayloadBody,
  buildBrokerPayload,
  type CredentialSubmitPayload,
  CredentialType,
  submitComplexLoginIdentities,
} from '@/sections/asset/components/tabs/credentials/credentialForms';
import { readConversationId } from '@/sections/asset/components/tabs/credentials/readConversationId';
import { wizardToRecipe } from '@/sections/asset/components/tabs/credentials/RecipeWizard/wizardConfig';
import type {
  SeedWizardFormData,
  WizardCredentialData,
} from '@/sections/seeds/components/modals/AddSeedByTypeModal/AddSeedByTypeModal.types';
// parseSeedLines is the CANONICAL, deduped seed split. This hook feeds seed
// CREATION while the credential steps feed ASSIGNMENT — they MUST share this one
// parser or credentials desync from the seeds actually created.
import {
  parseSeedLines,
  resolveAppCredential,
} from '@/sections/seeds/components/modals/AddSeedByTypeModal/steps/credentialAssign.util';
import { SeedTypeOption } from '@/types';

/**
 * Lifecycle status for a single submit stage.
 *  - idle: not started yet (or not applicable)
 *  - skipped: stage will not run for this submission (e.g. no creds configured)
 *  - pending: in flight
 *  - success: completed cleanly
 *  - error: failed
 */
export type SubmitStageStatus =
  | 'idle'
  | 'skipped'
  | 'pending'
  | 'success'
  | 'error';

/** Per-seed credential attach outcome (security P0-1(b): which seeds failed). */
export interface SeedCredentialResult {
  seed: string;
  ok: boolean;
}

interface UseAddSeedSubmitProps {
  formData: SeedWizardFormData;
  onSuccess: (seedType: SeedTypeOption, textInput: string) => void;
}

interface UseAddSeedSubmitReturn {
  handleSubmit: () => Promise<void>;
  /** True from submit click until both stages settle. */
  isPending: boolean;
  /** True once submit has run to completion (success or error). */
  isComplete: boolean;
  seedsStatus: SubmitStageStatus;
  credentialsStatus: SubmitStageStatus;
  /** Per-seed credential outcomes (empty when credentials are skipped). */
  credentialResults: SeedCredentialResult[];
  /** First error surfaced during submit, if any (sanitized — no secrets). */
  error: Error | null;
  /**
   * One row per COMPLEX_LOGIN identity that spawned a recorder conversation
   * (`id` = identity id for a stable React key + retry correlation; `label` =
   * the clean identity label; `conversationId` from the broker). Empty for
   * non-complex-login submits. Drives the STACKED per-identity recorder view —
   * the same shape Host A (AuthenticationTab) uses.
   */
  recorderResults: Array<{ id: string; label: string; conversationId: string }>;
  reset: () => void;
}

// Bound the number of concurrent broker POSTs. Single-row /broker rides a 29s
// Lambda/API-GW cap; a small bounded pool keeps a large pasted seed list from
// fanning out into an unbounded burst (security P0-1(d)).
const MAX_CONCURRENT_CREDENTIAL_POSTS = 4;

/**
 * Composite key for a (seed, identity) pair in `succeededIdentityKeys` (F3).
 * Keyed by ORDER INDEX within the identity array, not by identity id: in the
 * multi-app path `templateComplexForApp` mints a fresh `crypto.randomUUID()`
 * per identity on every submit, so ids are NOT stable across retries — the
 * index is (the fan-out preserves identity order). Index is stable across
 * value edits (editing an identity's fields doesn't reorder the array).
 */
const identityKey = (seedKey: string, index: number) => `${seedKey}::${index}`;

/**
 * Maps the wizard's controlled credential bundle to a CredentialSubmitPayload
 * for the active type, or null when there is nothing to attach (skip mode, or
 * a configure mode whose active form is empty/incomplete — e.g. a RECORDING
 * with no file, or a RECIPE whose draft cannot be converted). Returning null
 * makes "configure but left blank" behave like skip rather than erroring.
 */
function credentialToPayload(
  data: WizardCredentialData
): CredentialSubmitPayload | null {
  switch (data.selectedType) {
    case CredentialType.BASIC:
      if (!data.basicForm.username && !data.basicForm.password) return null;
      return { type: CredentialType.BASIC, data: data.basicForm };

    case CredentialType.RECORDING:
      if (!data.recordingForm.file) return null;
      return {
        type: CredentialType.RECORDING,
        data: { ...data.recordingForm, file: data.recordingForm.file },
      };

    case CredentialType.BEARER_TOKEN:
      if (!data.bearerTokenForm.token && !data.bearerTokenForm.isDynamic) {
        return null;
      }
      return { type: CredentialType.BEARER_TOKEN, data: data.bearerTokenForm };

    case CredentialType.API_KEY:
      if (!data.apiKeyForm.apiKey && !data.apiKeyForm.isDynamic) return null;
      return { type: CredentialType.API_KEY, data: data.apiKeyForm };

    case CredentialType.STATIC_TOKEN: {
      const hasHeader = data.staticTokenForm.headers.some(
        h => h.key && h.value
      );
      if (!hasHeader) return null;
      return { type: CredentialType.STATIC_TOKEN, data: data.staticTokenForm };
    }

    case CredentialType.RECIPE: {
      // The recipe is now built via the parent wizard's inline recipe steps,
      // which edit `recipeDraft`. Convert at submit (the boundary the broker
      // reads) via wizardToRecipe; fall back to a pre-converted `recipeForm` if
      // one was set. Reaching the terminal RECIPE_REVIEW step means the draft
      // already passed the Overview/Inputs/Request/Session validators, so it is
      // usable — but guard defensively against an empty draft.
      const recipe = data.recipeForm ?? wizardToRecipe(data.recipeDraft);
      return recipe ? { type: CredentialType.RECIPE, data: recipe } : null;
    }

    // COMPLEX_LOGIN is NOT a single static-credential payload — it fans out one
    // broker POST PER identity via submitComplexLoginIdentities. handleSubmit
    // branches on COMPLEX_LOGIN before ever reaching this mapper, so returning
    // null here is unreachable in practice; it keeps the switch sound without
    // building a (now non-existent) single complexLoginForm payload.
    case CredentialType.COMPLEX_LOGIN:
      return null;
  }
}

/**
 * Builds the broker body for a payload, reading the RECORDING file caller-side
 * (FileReader) so buildBrokerPayload stays pure (security cross-cutting #5).
 */
async function buildBody(
  payload: CredentialSubmitPayload,
  isWebService: boolean
): Promise<BrokerPayloadBody> {
  if (payload.type === CredentialType.RECORDING) {
    const script = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsText(payload.data.file);
    });
    return buildBrokerPayload(
      {
        type: CredentialType.RECORDING,
        data: { label: payload.data.label, script },
      },
      { isWebService }
    );
  }
  return buildBrokerPayload(payload, { isWebService });
}

/**
 * Produces a safe, user-facing error message (P2-1): status code + generic
 * text only. NEVER interpolates the request payload, headers, or a reflected
 * response body that could echo a secret the user typed.
 */
function sanitizeError(err: unknown): Error {
  if (axios.isAxiosError(err)) {
    const status = err.response?.status;
    return new Error(
      status
        ? `Request failed (HTTP ${status}). Please try again.`
        : 'Request failed. Please check your connection and try again.'
    );
  }
  return new Error('An unexpected error occurred. Please try again.');
}

/**
 * Runs `task` over `seeds` with bounded concurrency, collecting a per-seed
 * `{ seed, ok }` result for each (security P0-1(b): record every seed, never
 * abort on first failure). The seed is captured in the closure so a refactor
 * can't read a shared index (cross-cutting #4: per-iteration ResourceKey).
 */
async function runBounded(
  seeds: string[],
  task: (seed: string) => Promise<void>
): Promise<SeedCredentialResult[]> {
  const results: SeedCredentialResult[] = [];
  let cursor = 0;

  const worker = async () => {
    while (cursor < seeds.length) {
      const seed = seeds[cursor++];
      try {
        await task(seed);
        results.push({ seed, ok: true });
      } catch {
        results.push({ seed, ok: false });
      }
    }
  };

  const workerCount = Math.min(MAX_CONCURRENT_CREDENTIAL_POSTS, seeds.length);
  await Promise.all(Array.from({ length: workerCount }, () => worker()));
  return results;
}

/**
 * Hook for handling seed submission in the AddSeedByTypeModal.
 *
 * Drives a two-stage progress UI: stage 1 adds the seeds, stage 2 attaches the
 * configured credential to each web-app/web-service seed. Stage 2 is type-aware
 * (Basic/Bearer/ApiKey/Static/Recording/Recipe/ComplexLogin via the shared
 * buildBrokerPayload) and partial-failure safe:
 *  - onSuccess / done fire ONLY when both stages succeed (or credentials are
 *    skipped) — never on a credential failure (security P0-1(a));
 *  - every seed is attempted and individually reported (P0-1(b));
 *  - a retry re-attaches ONLY the failed seeds and never re-runs stage 1
 *    (P0-1(c)) — preventing the duplicate-HAS_CREDENTIAL spiral.
 */
export function useAddSeedSubmit({
  formData,
  onSuccess,
}: UseAddSeedSubmitProps): UseAddSeedSubmitReturn {
  const axiosInstance = useAxios();
  const { mutateAsync: bulkAddSeeds } = useAddSeeds();

  // Local pending state covers the full submit lifecycle (seeds add + per-seed
  // broker POSTs). bulkAddSeeds.isPending flips back to false the moment the
  // seeds mutation resolves, but the wizard keeps running async work after
  // that — relying on it alone re-enables the Submit button mid-flight and
  // lets users double-submit.
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [seedsStatus, setSeedsStatus] = useState<SubmitStageStatus>('idle');
  const [credentialsStatus, setCredentialsStatus] =
    useState<SubmitStageStatus>('idle');
  const [credentialResults, setCredentialResults] = useState<
    SeedCredentialResult[]
  >([]);
  // Persisted across submits so a retry re-attaches ONLY these seeds. Stage 1
  // is never re-run once seedsStatus is 'success' (P0-1(c)). Cleared by reset().
  const [seedsCreated, setSeedsCreated] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  // One row per COMPLEX_LOGIN identity that spawned a recorder conversation.
  // COMPLEX_LOGIN is single-seed (D4) and fans out over N identities, so this
  // holds up to N rows for the STACKED recorder view (mirror of Host A).
  const [recorderResults, setRecorderResults] = useState<
    Array<{ id: string; label: string; conversationId: string }>
  >([]);
  // Maps raw input line → burp-resolved primary_url (web-service seeds). Persisted
  // so a retry — which skips Stage 1 — still keys the broker attach off the
  // resolved URL the asset is stored under, not the raw input line.
  const [resolvedUrls, setResolvedUrls] = useState<Record<string, string>>({});
  // Composite keys "${seedKey}::${identityIndex}" for (seed, identity) pairs
  // that already succeeded (POST ok AND returned a conversationId). Persisted
  // across submits so a COMPLEX retry SKIPS them — preventing duplicate recorder
  // conversations / duplicate HAS_CREDENTIAL attachments (F3). Cleared by reset().
  const [succeededIdentityKeys, setSucceededIdentityKeys] = useState<
    Set<string>
  >(() => new Set());

  const reset = useCallback(() => {
    setIsSubmitting(false);
    setIsComplete(false);
    setSeedsStatus('idle');
    setCredentialsStatus('idle');
    setCredentialResults([]);
    setSeedsCreated(false);
    setError(null);
    setResolvedUrls({});
    setRecorderResults([]);
    setSucceededIdentityKeys(new Set());
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!formData.seedType || isSubmitting) return;

    const parsedSeeds = formData.textInput
      ? parseSeedLines(formData.textInput)
      : [];

    if (parsedSeeds.length === 0) return;

    const isWebService = formData.seedType === SeedTypeOption.WEB_SERVICE;

    // Multi-app path (seedCount > 1 web seeds): per-app credential fan-out. The
    // existing single `configure` / `skip` paths are byte-unchanged; this branch
    // sits BEFORE the single-seed COMPLEX branch and reuses the SAME two submit
    // primitives (runBounded static + submitComplexLoginIdentities COMPLEX).
    // Narrow once so the per-app resolver closures don't re-narrow the union.
    const multi =
      formData.credential.mode === 'configure-multi'
        ? formData.credential
        : null;

    /** Resolves the WizardCredentialData (COMPLEX templated to its own URL) for one app. */
    const resolveForApp = (appUrl: string): WizardCredentialData | null =>
      multi
        ? resolveAppCredential(
            multi.perApp[appUrl] ?? 'off',
            multi.shared,
            multi.custom[appUrl],
            appUrl
          )
        : null;

    // COMPLEX_LOGIN is NOT a single static-credential payload — it fans out one
    // broker POST per identity over the SINGLE web-app seed (D4). Detect it up
    // front (KISS) so handleSubmit can branch to the shared
    // submitComplexLoginIdentities fan-out instead of the per-seed static path.
    const isComplexLogin =
      formData.credential.mode === 'configure' &&
      formData.credential.data.selectedType === CredentialType.COMPLEX_LOGIN;

    // Resolve the static credential payload once (shared across all seeds). null
    // means skip — either Skip mode, a Configure mode left blank, or COMPLEX
    // (which uses the identity fan-out, not a single payload).
    const credentialPayload =
      formData.credential.mode === 'configure'
        ? credentialToPayload(formData.credential.data)
        : null;
    // For the multi-app path, creds attach when ANY app resolves a non-null
    // config; all-off resolves to nothing → Stage-1-only (credentialsStatus
    // 'skipped'). COMPLEX/static single paths gate on their own predicates.
    const willAttachCreds = multi
      ? parsedSeeds.some(appUrl => resolveForApp(appUrl) !== null)
      : credentialPayload !== null || isComplexLogin;

    setIsSubmitting(true);
    setIsComplete(false);
    setError(null);

    // Stage 1: add seeds. Skipped entirely on a retry — seeds already exist, so
    // re-running would create duplicates / re-POST to succeeded seeds (P0-1(c)).
    // Seeded from prior state for the retry path (Stage 1 is skipped on retry).
    let resolved = resolvedUrls;
    if (!seedsCreated) {
      setSeedsStatus('pending');
      setCredentialsStatus(willAttachCreds ? 'idle' : 'skipped');
      try {
        const addResult = await bulkAddSeeds({
          seeds: parsedSeeds,
          type: formData.seedType,
        });
        resolved = addResult?.resolvedUrls ?? {};
        setResolvedUrls(resolved);
        setSeedsStatus('success');
        setSeedsCreated(true);
      } catch (err) {
        setSeedsStatus('error');
        setError(sanitizeError(err));
        setIsSubmitting(false);
        setIsComplete(true);
        // Stage 1 failed: do NOT signal success.
        return;
      }
    }

    // Stage 2 (CONFIGURE-MULTI): per-app credential fan-out. Reuses the SAME two
    // submit primitives the single paths use — runBounded for static apps and
    // submitComplexLoginIdentities for COMPLEX apps — keyed PER APP. No new
    // /broker call site. Each app's resolved config (resolveForApp) already
    // templates a COMPLEX loginUrl to that app's own URL (§2.1) and a Custom
    // RECIPE was authored against its own URL (Option B), so no double-templating
    // happens here. Per-app retry (decision #5): on a retry, only the apps that
    // previously FAILED are re-attempted; Stage 1 is already skipped via
    // seedsCreated.
    if (multi) {
      if (!willAttachCreds) {
        // All rows 'off' (or shared/custom unconfigured) → seeds created, no
        // credential POSTs. credentialsStatus was set 'skipped' in Stage 1.
        setIsSubmitting(false);
        setIsComplete(true);
        onSuccess(formData.seedType, formData.textInput);
        return;
      }

      setCredentialsStatus('pending');

      // Apps that have a credential to attach. On retry, narrow to only the apps
      // that previously failed (mirrors the single static path's filter at the
      // per-app granularity, decision #5); first attempt targets all assigned.
      const assignedApps = parsedSeeds.filter(
        appUrl => resolveForApp(appUrl) !== null
      );
      const targetApps =
        credentialResults.length > 0
          ? assignedApps.filter(appUrl =>
              credentialResults.some(r => r.seed === appUrl && !r.ok)
            )
          : assignedApps;

      // Split targets by submit primitive: COMPLEX_LOGIN fans out per identity;
      // every other resolved type rides the static one-POST-per-app path.
      const complexApps: string[] = [];
      const staticApps: string[] = [];
      for (const appUrl of targetApps) {
        const resolved = resolveForApp(appUrl);
        if (!resolved) continue;
        if (resolved.selectedType === CredentialType.COMPLEX_LOGIN) {
          complexApps.push(appUrl);
        } else {
          staticApps.push(appUrl);
        }
      }

      // --- Static apps: reuse runBounded; build each app's body per-iteration
      // (custom configs differ per app), POST to that app's own ResourceKey. ---
      let staticResults: SeedCredentialResult[] = [];
      let staticError: Error | null = null;
      if (staticApps.length > 0) {
        staticResults = await runBounded(staticApps, async appUrl => {
          const appConfig = resolveForApp(appUrl);
          // Unreachable (staticApps came from non-null, non-COMPLEX resolves),
          // but keeps the closure total for the type system.
          if (!appConfig) return;
          const payload = credentialToPayload(appConfig);
          if (!payload) {
            // Defense-in-depth (F1): an ASSIGNED app whose resolved bundle
            // yields no payload is a real misconfiguration — surface it as a
            // per-app failure instead of silently reporting ok. runBounded's
            // catch records ok:false.
            throw new Error('No credential data');
          }
          const body = await buildBody(payload, isWebService);
          // Web-service seeds are stored under the burp-resolved primary_url;
          // fall back to the app URL itself for types that don't remap. Same
          // per-iteration ResourceKey rule as the single static path.
          const resourceUrl = resolved[appUrl] ?? appUrl;
          await axiosInstance.post('/broker', {
            Category: 'env-integration',
            Operation: 'add',
            ResourceKey: `#webapplication#${resourceUrl}`,
            ...body,
          });
        });
      }

      // --- COMPLEX apps: reuse submitComplexLoginIdentities per app; each app's
      // identities already carry that app's templated loginUrl + fresh ids. ---
      const complexResults: SeedCredentialResult[] = [];
      const complexRecorderRows: Array<{
        id: string;
        label: string;
        conversationId: string;
      }> = [];
      // Accumulate newly-succeeded (appUrl, index) pairs locally and commit once
      // after the loop (avoids stale-closure churn from setState-in-loop). Seeded
      // from the prior set so cross-attempt successes carry forward (F3).
      const newlyOk = new Set(succeededIdentityKeys);
      for (const appUrl of complexApps) {
        const appConfig = resolveForApp(appUrl);
        if (!appConfig) continue;
        const resourceUrl = resolved[appUrl] ?? appUrl;
        const resourceKey = `#webapplication#${resourceUrl}`;
        // Ids churn per submit (templateComplexForApp mints fresh UUIDs), so key
        // on ORDER INDEX. Skip identities that already succeeded on a prior
        // attempt; only fan out the not-yet-succeeded ones (F3 retry-only-failed).
        const templated = appConfig.complexLogin.identities;
        const pending = templated
          .map((identity, index) => ({ identity, index }))
          .filter(({ index }) => !newlyOk.has(identityKey(appUrl, index)));

        // Every identity for this app already succeeded → nothing to POST; the
        // app is fully authenticated (guards against re-running a succeeded app).
        if (pending.length === 0) {
          complexResults.push({ seed: appUrl, ok: true });
          continue;
        }

        try {
          const identityResults = await submitComplexLoginIdentities({
            multi: {
              ...appConfig.complexLogin,
              identities: pending.map(p => p.identity),
            },
            submitOne: async brokerPayload => {
              const response = await axiosInstance.post('/broker', {
                Category: 'env-integration',
                Operation: 'add',
                ResourceKey: resourceKey,
                ...brokerPayload,
              });
              return {
                conversationId: readConversationId(response.data) ?? undefined,
              };
            },
            limit: MAX_CONCURRENT_CREDENTIAL_POSTS,
          });
          // Results preserve identity order over `pending`; map each back to its
          // ORIGINAL index to record the (appUrl, index) success. One recorder row
          // per identity that returned a conversation id (ids are fresh-per-app,
          // so rows never collide across apps — recorderResults keys on id, §1.4).
          identityResults.forEach((r, i) => {
            if (r.ok && r.conversationId) {
              newlyOk.add(identityKey(appUrl, pending[i].index));
              complexRecorderRows.push({
                id: r.id,
                label: r.label,
                conversationId: r.conversationId,
              });
            }
          });
          // The app succeeds only when EVERY original identity (across attempts)
          // is now recorded — skipped-as-already-succeeded identities count too.
          const appOk = templated.every((_id, index) =>
            newlyOk.has(identityKey(appUrl, index))
          );
          complexResults.push({ seed: appUrl, ok: appOk });
        } catch (err) {
          // Unexpected (non-per-identity) error — record the app as failed and
          // surface a sanitized message.
          complexResults.push({ seed: appUrl, ok: false });
          staticError = staticError ?? sanitizeError(err);
        }
      }
      setSucceededIdentityKeys(newlyOk);

      // Merge this attempt's per-app outcomes over any prior results so apps that
      // already succeeded stay ok and only re-attempted apps update. Apps with no
      // credential resolve to ok (seed created, nothing to attach).
      const attempted = [...staticResults, ...complexResults];
      setCredentialResults(prev => {
        const byApp = new Map(prev.map(r => [r.seed, r] as const));
        for (const result of attempted) byApp.set(result.seed, result);
        return assignedApps.map(
          appUrl => byApp.get(appUrl) ?? { seed: appUrl, ok: true }
        );
      });

      // Stack the new COMPLEX recorder rows alongside any from a prior attempt
      // (retry of a failed COMPLEX app spawns fresh conversations for THAT app).
      if (complexRecorderRows.length > 0) {
        setRecorderResults(prev => [...prev, ...complexRecorderRows]);
      }

      const allCredsOk =
        attempted.every(r => r.ok) &&
        // Account for prior-attempt successes outside this attempt's targets.
        assignedApps.every(
          appUrl =>
            attempted.some(r => r.seed === appUrl && r.ok) ||
            credentialResults.some(r => r.seed === appUrl && r.ok)
        );
      setCredentialsStatus(allCredsOk ? 'success' : 'error');
      if (!allCredsOk) {
        setError(
          staticError ??
            new Error(
              'Some apps could not be authenticated. Retry to re-attach only the failed ones.'
            )
        );
      }

      setIsSubmitting(false);
      setIsComplete(true);
      // onSuccess ONLY when every app + identity succeeded (never on a partial
      // failure — P0-1(a)).
      if (allCredsOk) {
        onSuccess(formData.seedType, formData.textInput);
      }
      return;
    }

    // Stage 2 (COMPLEX_LOGIN): fan out one broker POST PER identity over the
    // SINGLE web-app seed via the shared submitComplexLoginIdentities helper
    // (D4 — COMPLEX is single-seed; the UI gate already enforces this, and we
    // defensively target only the first parsed seed here). Each identity carries
    // its own login_url and spawns its own recorder conversation. This mirrors
    // the asset drawer host's Complex-Login submit
    // (CredentialModals.handleSubmitComplexLogin).
    if (isComplexLogin && formData.credential.mode === 'configure') {
      setCredentialsStatus('pending');

      // Single target (Option A): the first parsed seed only. Resolve to the
      // burp-stored primary_url where applicable (matches the static path).
      const seed = parsedSeeds[0];
      const resourceUrl = resolved[seed] ?? seed;
      const resourceKey = `#webapplication#${resourceUrl}`;
      const allIdentities = formData.credential.data.complexLogin.identities;

      // Retry-only-failed (F3): skip identities whose (seed, index) already
      // succeeded on a prior attempt; only fan out the not-yet-succeeded ones.
      // Keep each pending identity's ORIGINAL index so the composite key is
      // stable across attempts. (Single-seed ids ARE stable — read directly from
      // formData — but we key on index for uniformity with the multi-app path.)
      const pending = allIdentities
        .map((identity, index) => ({ identity, index }))
        .filter(
          ({ index }) => !succeededIdentityKeys.has(identityKey(seed, index))
        );

      // All identities already succeeded (retry with nothing left) → treat as a
      // full success with ZERO POSTs; fire onSuccess (P0-1(a)) and return.
      if (pending.length === 0) {
        setCredentialResults([]);
        setCredentialsStatus('success');
        setIsSubmitting(false);
        setIsComplete(true);
        onSuccess(formData.seedType, formData.textInput);
        return;
      }

      const multi = {
        ...formData.credential.data.complexLogin,
        identities: pending.map(p => p.identity),
      };

      let results;
      try {
        results = await submitComplexLoginIdentities({
          multi,
          // submitOne is the RAW broker POST — it MUST NOT call
          // buildBrokerPayload (the helper already builds the per-identity
          // payload). It only wraps the envelope and reads the conversation id.
          submitOne: async brokerPayload => {
            const response = await axiosInstance.post('/broker', {
              Category: 'env-integration',
              Operation: 'add',
              ResourceKey: resourceKey,
              ...brokerPayload,
            });
            return {
              conversationId: readConversationId(response.data) ?? undefined,
            };
          },
          limit: MAX_CONCURRENT_CREDENTIAL_POSTS,
        });
      } catch (err) {
        // The helper isolates per-identity failures, so a throw here is an
        // unexpected (non-per-identity) error — surface it sanitized.
        setCredentialsStatus('error');
        setError(sanitizeError(err));
        setIsSubmitting(false);
        setIsComplete(true);
        return;
      }

      // Record newly-succeeded (seed, index) pairs (F3). Results preserve the
      // order of `pending`, so map each result back to its ORIGINAL index.
      const newlyOk = new Set(succeededIdentityKeys);
      results.forEach((r, i) => {
        if (r.ok && r.conversationId) {
          newlyOk.add(identityKey(seed, pending[i].index));
        }
      });
      setSucceededIdentityKeys(newlyOk);

      // STACKED recorder rows: one per identity in THIS attempt that returned a
      // conversation id. APPEND to prior rows (mirror the multi-app append) so a
      // retry doesn't wipe the recorder rows from the first attempt; since we
      // only submit `pending`, retries never append duplicate rows for
      // already-succeeded identities.
      const withConversations = results.flatMap(r =>
        r.ok && r.conversationId
          ? [{ id: r.id, label: r.label, conversationId: r.conversationId }]
          : []
      );
      if (withConversations.length > 0) {
        setRecorderResults(prev => [...prev, ...withConversations]);
      }

      // Per-identity partial-failure (preserve the spirit of P0-1): surface each
      // FAILED identity of THIS attempt in the per-row results display (keyed by
      // its clean label — labels are not secrets). An identity is failed if its
      // POST errored OR returned no conversation id (a 2xx POST with no
      // conversation_id means no recorder run started — nothing to watch).
      // The stage is a success only when EVERY identity across the FULL set is
      // now accounted for as succeeded — skipped-as-already-succeeded count too.
      const failed = results.filter(r => !r.ok || !r.conversationId);
      const allOk =
        failed.length === 0 &&
        allIdentities.every((_id, index) =>
          newlyOk.has(identityKey(seed, index))
        );
      setCredentialResults(failed.map(r => ({ seed: r.label, ok: false })));
      setCredentialsStatus(allOk ? 'success' : 'error');
      if (!allOk) {
        // Generic, no secrets (consistent with sanitizeError). A retry now
        // re-runs ONLY the failed identities (F3) — Stage 1 stays skipped via
        // seedsCreated and succeeded identities are skipped via
        // succeededIdentityKeys.
        setError(
          new Error(
            'Some identities could not be recorded. Review the failures below.'
          )
        );
      }

      setIsSubmitting(false);
      setIsComplete(true);
      // onSuccess ONLY when every identity succeeded (never on a credential
      // failure — P0-1(a)).
      if (allOk) {
        onSuccess(formData.seedType, formData.textInput);
      }
      return;
    }

    // Stage 2: attach credentials (only when configured). On a retry, only the
    // seeds that previously failed are re-attempted.
    let allCredsOk = true;
    if (willAttachCreds && credentialPayload) {
      setCredentialsStatus('pending');

      const targetSeeds =
        credentialResults.length > 0
          ? credentialResults.filter(r => !r.ok).map(r => r.seed)
          : parsedSeeds;

      // Build the per-seed body once if file-reading is required; the body is
      // identical across seeds (only ResourceKey differs per-iteration).
      let body: BrokerPayloadBody;
      try {
        body = await buildBody(credentialPayload, isWebService);
      } catch (err) {
        setCredentialsStatus('error');
        setError(sanitizeError(err));
        setIsSubmitting(false);
        setIsComplete(true);
        return;
      }

      // Static credential attach: one broker POST per seed (COMPLEX_LOGIN took
      // its own identity-fan-out branch above and returned, so this path only
      // runs for Basic/Bearer/ApiKey/Static/Recording/Recipe).
      const attempted = await runBounded(targetSeeds, async seed => {
        // Web-service seeds are stored under the burp-resolved primary_url, not the
        // raw input line; fall back to the raw seed for types that don't remap.
        // Per-iteration ResourceKey from the captured seed (P0-1, no shared
        // index → no wrong-key cross-attach).
        const resourceUrl = resolved[seed] ?? seed;
        await axiosInstance.post('/broker', {
          Category: 'env-integration',
          Operation: 'add',
          ResourceKey: `#webapplication#${resourceUrl}`,
          ...body,
        });
      });

      // Merge this attempt's outcomes over any prior results so already-succeeded
      // seeds stay marked ok and only re-attempted seeds update.
      setCredentialResults(prev => {
        const bySeed = new Map(prev.map(r => [r.seed, r] as const));
        for (const result of attempted) bySeed.set(result.seed, result);
        return parsedSeeds.map(seed => bySeed.get(seed) ?? { seed, ok: true });
      });

      allCredsOk = attempted.every(r => r.ok);
      setCredentialsStatus(allCredsOk ? 'success' : 'error');
      if (!allCredsOk) {
        setError(
          new Error(
            'Some seeds could not be authenticated. Retry to re-attach only the failed ones.'
          )
        );
      }
    }

    setIsSubmitting(false);
    setIsComplete(true);

    // onSuccess ONLY when everything succeeded (or credentials were skipped) —
    // never on a credential failure (security P0-1(a)).
    if (allCredsOk) {
      onSuccess(formData.seedType, formData.textInput);
    }
  }, [
    formData,
    isSubmitting,
    seedsCreated,
    credentialResults,
    resolvedUrls,
    succeededIdentityKeys,
    bulkAddSeeds,
    axiosInstance,
    onSuccess,
  ]);

  return {
    handleSubmit,
    isPending: isSubmitting,
    isComplete,
    seedsStatus,
    credentialsStatus,
    credentialResults,
    error,
    recorderResults,
    reset,
  };
}
